import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Paper,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  Slide,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Science as ScienceIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  TrendingUp,
  People,
  Assessment,
  Security,
  Speed,
  ArrowForward,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface QuickActionCard {
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  color: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
  adminOnly?: boolean;
  gradient?: string;
  stats?: string;
}

interface DashboardStats {
  totalImages: number;
  totalAnalyses: number;
  totalUsers: number;
  recentActivity: number;
  pendingAnalyses: number;
  systemHealth: 'excellent' | 'good' | 'warning' | 'critical';
}

const getQuickActions = (isAdmin: boolean): QuickActionCard[] => [
  {
    title: 'Upload Image',
    description: 'Upload a new skin lesion image for preliminary evaluation',
    icon: <UploadIcon />,
    path: '/upload',
    color: 'primary',
    gradient: 'linear-gradient(135deg, #7D1F8E 0%, #B83C78 100%)',
    stats: 'Start Analysis',
  },
  {
    title: 'Advanced Analysis',
    description: 'Perform detailed ABCDE analysis on pending images',
    icon: <ScienceIcon />,
    path: '/analysis',
    color: 'secondary',
    gradient: 'linear-gradient(135deg, #EF7504 0%, #D2565A 100%)',
    stats: 'AI-Powered',
  },
  {
    title: 'View History',
    description: 'Review your previous evaluations and results',
    icon: <HistoryIcon />,
    path: '/history',
    color: 'success',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    stats: 'Track Progress',
  },
  ...(isAdmin ? [{
    title: 'System Analytics',
    description: 'View comprehensive system statistics and user insights',
    icon: <AnalyticsIcon />,
    path: '/analytics',
    color: 'info' as const,
    adminOnly: true,
    gradient: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)',
    stats: 'Admin Only',
  }] : []),
];

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const theme = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);

  const quickActions = getQuickActions(isAdmin);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Simulate API call for now - replace with actual API calls
        await new Promise(resolve => setTimeout(resolve, 1000));

        setStats({
          totalImages: 156,
          totalAnalyses: 89,
          totalUsers: isAdmin ? 23 : 1,
          recentActivity: 12,
          pendingAnalyses: 5,
          systemHealth: 'excellent',
        });
      } catch (err) {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAdmin]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate refresh
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Fade in={true} timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Welcome back, {user?.username}! 👋
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                {isAdmin ? 'Administrator Dashboard' : 'Your MELIA Dashboard'} • {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>

            <Tooltip title="Refresh Dashboard">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  background: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.2),
                    transform: 'rotate(180deg)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Welcome Card */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  🩺 MELIA - AI-Powered Melanoma Detection
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Advanced artificial intelligence system for preliminary skin lesion evaluation.
                  Upload images, perform ABCDE analysis, and track your skin health over time.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<Security />}
                    label="HIPAA Compliant"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<Speed />}
                    label="Real-time Analysis"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                  {isAdmin && (
                    <Chip
                      icon={<Assessment />}
                      label="Admin Access"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '2rem',
                    fontWeight: 700,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  {user ? user.username.substring(0, 2).toUpperCase() : 'U'}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {isAdmin ? 'System Administrator' : 'Registered User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Account Status: Active
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Fade>

      {/* Statistics Cards - Admin Only */}
      {isAdmin && stats && (
        <Slide direction="up" in={true} timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              📊 System Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                      <UploadIcon sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                        {stats.totalImages}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Images
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.2)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 56, height: 56 }}>
                      <ScienceIcon sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.secondary.main }}>
                        {stats.totalAnalyses}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Analyses Completed
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.2)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: theme.palette.success.main, width: 56, height: 56 }}>
                      <People sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.success.main }}>
                        {stats.totalUsers}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Users
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.info.main, 0.2)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: theme.palette.info.main, width: 56, height: 56 }}>
                      <TrendingUp sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.info.main }}>
                        {stats.recentActivity}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Recent Activity
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Slide>
      )}

      {/* Quick Actions */}
      <Fade in={true} timeout={1200}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            🚀 Quick Actions
          </Typography>
          <Grid container spacing={3}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={quickActions.length === 4 ? 3 : 4} key={action.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    background: action.gradient || `linear-gradient(135deg, ${alpha(theme.palette[action.color].main, 0.1)} 0%, ${alpha(theme.palette[action.color].main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette[action.color].main, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: `0 20px 40px ${alpha(theme.palette[action.color].main, 0.3)}`,
                      '& .action-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                      '& .action-arrow': {
                        transform: 'translateX(4px)',
                      },
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: action.gradient || theme.palette[action.color].main,
                    },
                  }}
                  onClick={() => handleNavigation(action.path)}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" spacing={3} alignItems="flex-start">
                      <Avatar
                        className="action-icon"
                        sx={{
                          bgcolor: theme.palette[action.color].main,
                          width: 64,
                          height: 64,
                          transition: 'all 0.3s ease',
                          boxShadow: `0 8px 24px ${alpha(theme.palette[action.color].main, 0.3)}`,
                        }}
                      >
                        {action.icon}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {action.title}
                          </Typography>
                          {action.stats && (
                            <Chip
                              label={action.stats}
                              size="small"
                              color={action.color}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                          {action.description}
                        </Typography>
                        <Button
                          variant="contained"
                          color={action.color}
                          endIcon={<ArrowForward className="action-arrow" sx={{ transition: 'transform 0.3s ease' }} />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            background: action.gradient || undefined,
                            '&:hover': {
                              background: action.gradient ?
                                action.gradient.replace('135deg', '135deg').replace('0%', '10%').replace('100%', '90%') :
                                undefined,
                            },
                          }}
                        >
                          Get Started
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Fade>

      {/* Medical Disclaimer */}
      <Fade in={true} timeout={1400}>
        <Alert
          severity="info"
          sx={{
            mt: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            '& .MuiAlert-icon': {
              fontSize: '1.5rem',
            },
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            <strong>⚠️ Medical Disclaimer:</strong> MELIA provides AI-assisted preliminary evaluation for educational and preventive monitoring purposes only.
            This tool does NOT constitute medical diagnosis and should not replace professional medical evaluation.
            Always consult with qualified healthcare professionals for proper skin health assessment and medical advice.
          </Typography>
        </Alert>
      </Fade>

    </Box>
  );
};

export default DashboardPage;
